import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Loader2, Printer } from 'lucide-react';
import useGeoData from '../../../store/useGeoData';
import { usePrintMaps } from '../hooks/usePrintMaps';
import type { PrintUnit, PrintOutputFormat } from '../../../store/printMapsSlice';
import { SelectInput, type SelectOption } from '../../../common/inputs/SelectInput/SelectInput';
import { NumberInput } from '../../../common/inputs/NumberInput/NumberInput';
import ToggleButton from '../../../common/inputs/ToggleButton/ToggleButton';
import MenuButton from '../../../common/MenuButton/MenuButton';
import { FeedbackBox } from '../../../common/FeedbackBox/FeedbackBox';
import styles from './PrintMapsPanel.module.css';

const PrintMapsPanel: React.FC = () => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const originalMapSize = useRef<{width: string, height: string} | null>(null);

  // Select options
  const unitOptions: SelectOption[] = [
    { value: 'in', label: 'Inches' },
    { value: 'mm', label: 'Millimeters' }
  ];

  const formatOptions: SelectOption[] = [
    { value: 'png', label: 'PNG' },
    { value: 'pdf', label: 'PDF' }
  ];

  // Zustand state and actions
  const {
    printWidth,
    printHeight,
    printDpi,
    printUnit,
    printOutputFormat,
    isGenerating,
    showPrintPreview,
    fitPreviewToContainer,
    setPrintWidth,
    setPrintHeight,
    setPrintDpi,
    setPrintUnit,
    setPrintOutputFormat,
    setShowPrintPreview,
    setFitPreviewToContainer,
    resetPrintConfig,
    mapInstance
  } = useGeoData(useShallow((state) => ({
    printWidth: state.printWidth,
    printHeight: state.printHeight,
    printDpi: state.printDpi,
    printUnit: state.printUnit,
    printOutputFormat: state.printOutputFormat,
    isGenerating: state.isGenerating,
    showPrintPreview: state.showPrintPreview,
    fitPreviewToContainer: state.fitPreviewToContainer,
    setPrintWidth: state.setPrintWidth,
    setPrintHeight: state.setPrintHeight,
    setPrintDpi: state.setPrintDpi,
    setPrintUnit: state.setPrintUnit,
    setPrintOutputFormat: state.setPrintOutputFormat,
    setShowPrintPreview: state.setShowPrintPreview,
    setFitPreviewToContainer: state.setFitPreviewToContainer,
    resetPrintConfig: state.resetPrintConfig,
    mapInstance: state.mapInstance
  })));

  const { generatePrintMap } = usePrintMaps();

  // Get WebGL limits for canvas size validation
  const getWebGLMaxSize = useCallback(() => {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (gl && 'getParameter' in gl) {
        return (gl as WebGLRenderingContext).getParameter((gl as WebGLRenderingContext).MAX_RENDERBUFFER_SIZE);
      }
    } catch {
      console.warn('Could not get WebGL context for size limits');
    }
    return 16384; // Default fallback
  }, []);

  // Check if canvas dimensions will be capped
  const checkCanvasSizeCapped = useCallback(() => {
    if (printWidth <= 0 || printHeight <= 0 || printDpi <= 0) return { isCapped: false, maxSize: 0 };
    
    const conversionFactor = printUnit === 'mm' ? 96 / 25.4 : 96;
    const finalWidthPx = Math.round(conversionFactor * printWidth * (printDpi / 96));
    const finalHeightPx = Math.round(conversionFactor * printHeight * (printDpi / 96));
    const maxSize = getWebGLMaxSize();
    
    const isCapped = finalWidthPx > maxSize || finalHeightPx > maxSize;
    
    return { isCapped, maxSize, finalWidthPx, finalHeightPx };
  }, [printWidth, printHeight, printDpi, printUnit, getWebGLMaxSize]);

  // Effect to handle preview toggle - save/restore map size
  useEffect(() => {
    const mapContainer = document.getElementById('map-container');
    if (!mapContainer || !mapInstance) return;

    // Find the map wrapper (parent of map-container)
    const mapWrapper = mapContainer.parentElement;

    if (showPrintPreview) {
      // Save original size ONLY if not already saved
      if (!originalMapSize.current) {
        originalMapSize.current = {
          width: mapContainer.style.width || '100%',
          height: mapContainer.style.height || '100%'
        };
      }
      
      // Apply print dimensions
      if (printWidth > 0 && printHeight > 0) {
        const conversionFactor = printUnit === 'mm' ? 96 / 25.4 : 96;
        const printWidthPx = conversionFactor * printWidth;
        const printHeightPx = conversionFactor * printHeight;
        
        // Always set the real print dimensions
        mapContainer.style.width = `${printWidthPx}px`;
        mapContainer.style.height = `${printHeightPx}px`;
        
        // If fit to container is enabled, use CSS transform to scale
        if (fitPreviewToContainer && mapWrapper) {
          const wrapperRect = mapWrapper.getBoundingClientRect();
          const availableWidth = wrapperRect.width - 20; // Some padding
          const availableHeight = wrapperRect.height - 20; // Some padding
          
          // Calculate scale factor to fit both dimensions
          const scaleX = availableWidth / printWidthPx;
          const scaleY = availableHeight / printHeightPx;
          const scale = Math.min(scaleX, scaleY, 1); // Don't scale up, only down
          
          // Apply CSS transform scale
          mapContainer.style.transform = `scale(${scale})`;
          mapContainer.style.transformOrigin = 'top left';
          
          // Center the scaled map in the wrapper
          const scaledWidth = printWidthPx * scale;
          const scaledHeight = printHeightPx * scale;
          const offsetX = (wrapperRect.width - scaledWidth) / 2;
          const offsetY = (wrapperRect.height - scaledHeight) / 2;
          mapContainer.style.marginLeft = `${offsetX}px`;
          mapContainer.style.marginTop = `${offsetY}px`;
          
          mapWrapper.style.overflow = 'hidden';
          
          // Add pattern background
          mapWrapper.style.backgroundImage = `
            linear-gradient(45deg, rgba(0,0,0,0.3) 25%, transparent 25%), 
            linear-gradient(-45deg, rgba(0,0,0,0.3) 25%, transparent 25%), 
            linear-gradient(45deg, transparent 75%, rgba(0,0,0,0.3) 75%), 
            linear-gradient(-45deg, transparent 75%, rgba(0,0,0,0.3) 75%)
          `;
          mapWrapper.style.backgroundSize = '20px 20px';
          mapWrapper.style.backgroundPosition = '0 0, 0 10px, 10px -10px, -10px 0px';
        } else {
          // Reset transform and margins
          mapContainer.style.transform = '';
          mapContainer.style.transformOrigin = '';
          mapContainer.style.marginLeft = '';
          mapContainer.style.marginTop = '';
          
          if (mapWrapper) {
            mapWrapper.style.overflow = 'auto';
            // Remove pattern background
            mapWrapper.style.backgroundImage = '';
            mapWrapper.style.backgroundSize = '';
            mapWrapper.style.backgroundPosition = '';
          }
        }
        
        mapInstance.resize();
      }
    } else {
      // Restore original size and overflow
      if (originalMapSize.current) {
        mapContainer.style.width = originalMapSize.current.width;
        mapContainer.style.height = originalMapSize.current.height;
        
        // Reset transform and margins
        mapContainer.style.transform = '';
        mapContainer.style.transformOrigin = '';
        mapContainer.style.marginLeft = '';
        mapContainer.style.marginTop = '';
        
        // Restore original overflow and remove pattern
        if (mapWrapper) {
          mapWrapper.style.overflow = 'hidden';
          mapWrapper.style.backgroundImage = '';
          mapWrapper.style.backgroundSize = '';
          mapWrapper.style.backgroundPosition = '';
        }
        
        mapInstance.resize();
        originalMapSize.current = null;
      }
    }
  }, [showPrintPreview, mapInstance, printWidth, printHeight, printUnit, fitPreviewToContainer]);

  // Validation helpers
  const validateNumber = useCallback((value: string, fieldName: string, min = 0): string | null => {
    const num = parseFloat(value);
    if (isNaN(num) || num <= min) {
      return `${fieldName} must be a positive number!`;
    }
    return null;
  }, []);

  // Removed artificial dimension validation - let the browser and WebGL handle limits naturally

  // Event handlers
  const handleWidthChange = (value: number | string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
    setPrintWidth(numValue);
    
    const error = validateNumber(value.toString(), 'Width');
    if (error) {
      setErrors(prev => ({ ...prev, width: error }));
    } else {
      setErrors(prev => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { width, ...rest } = prev;
        return rest;
      });
      
      // Resize map like original app (only when preview is active)
      if (showPrintPreview && mapInstance && numValue > 0 && printHeight > 0) {
        const mapContainer = document.getElementById('map-container');
        if (mapContainer) {
          const conversionFactor = printUnit === 'mm' ? 96 / 25.4 : 96;
          mapContainer.style.width = `${conversionFactor * numValue}px`;
          mapContainer.style.height = `${conversionFactor * printHeight}px`;
          mapInstance.resize();
        }
      }
    }
  };

  const handleHeightChange = (value: number | string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
    setPrintHeight(numValue);
    
    const error = validateNumber(value.toString(), 'Height');
    if (error) {
      setErrors(prev => ({ ...prev, height: error }));
    } else {
      setErrors(prev => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { height, ...rest } = prev;
        return rest;
      });
      
      // Resize map like original app (only when preview is active)
      if (showPrintPreview && mapInstance && numValue > 0 && printWidth > 0) {
        const mapContainer = document.getElementById('map-container');
        if (mapContainer) {
          const conversionFactor = printUnit === 'mm' ? 96 / 25.4 : 96;
          mapContainer.style.width = `${conversionFactor * printWidth}px`;
          mapContainer.style.height = `${conversionFactor * numValue}px`;
          mapInstance.resize();
        }
      }
    }
  };

  const handleDpiChange = (value: number | string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
    setPrintDpi(numValue);
    
    const error = validateNumber(value.toString(), 'DPI', 50);
    if (error) {
      setErrors(prev => ({ ...prev, dpi: error }));
    } else {
      setErrors(prev => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { dpi, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newUnit = e.target.value as PrintUnit;
    
    // Convert dimensions when changing units
    if (newUnit !== printUnit) {
      let newWidth = printWidth;
      let newHeight = printHeight;
      
      if (newUnit === 'mm' && printUnit === 'in') {
        newWidth = printWidth * 25.4;
        newHeight = printHeight * 25.4;
      } else if (newUnit === 'in' && printUnit === 'mm') {
        newWidth = printWidth / 25.4;
        newHeight = printHeight / 25.4;
      }
      
      setPrintWidth(newWidth);
      setPrintHeight(newHeight);
      
      // Clear any existing validation errors after unit conversion
      setErrors({});
    }
    
    setPrintUnit(newUnit);
  };

  const handleFormatChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPrintOutputFormat(e.target.value as PrintOutputFormat);
  };

  const handleGenerate = async () => {
    // Clear previous errors
    setErrors({});
    
    // Validate inputs
    const widthError = validateNumber(printWidth.toString(), 'Width');
    const heightError = validateNumber(printHeight.toString(), 'Height');
    const dpiError = validateNumber(printDpi.toString(), 'DPI', 50);
    
    const newErrors: Record<string, string> = {};
    if (widthError) newErrors.width = widthError;
    if (heightError) newErrors.height = heightError;
    if (dpiError) newErrors.dpi = dpiError;
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    if (!mapInstance) {
      setErrors({ general: 'No map instance available' });
      return;
    }
    
    try {
      await generatePrintMap();
    } catch (error) {
      console.error('Print generation failed:', error);
      setErrors({ 
        general: error instanceof Error ? error.message : 'Failed to generate print map' 
      });
    }
  };

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-s)' }}>
      {/* Preview Toggle */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <ToggleButton
          id="print-preview-toggle"
          isOn={showPrintPreview}
          onToggle={() => setShowPrintPreview(!showPrintPreview)}
          disabled={isGenerating}
          label="Show Preview"
        />
      </div>

      {/* Fit to Container Toggle - only show when preview is active */}
      {showPrintPreview && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <ToggleButton
            id="fit-preview-toggle"
            isOn={fitPreviewToContainer}
            onToggle={() => setFitPreviewToContainer(!fitPreviewToContainer)}
            disabled={isGenerating}
            label="Fit to Container"
          />
        </div>
      )}

      {/* Unit Selection */}
      <div className="style-control">
        <label htmlFor="unit-select" className="style-label">Unit</label>
        <div className="style-control-input-wrapper">
          <SelectInput
            options={unitOptions}
            value={printUnit}
            onChange={handleUnitChange}
            disabled={isGenerating}
          />
        </div>
      </div>

      {/* Output Format */}
      <div className="style-control">
        <label htmlFor="format-select" className="style-label">Output Format</label>
        <div className="style-control-input-wrapper">
          <SelectInput
            options={formatOptions}
            value={printOutputFormat}
            onChange={handleFormatChange}
            disabled={isGenerating}
          />
        </div>
      </div>

      {/* Width */}
      <div className="style-control">
        <label htmlFor="width" className="style-label">Width</label>
        <div className="style-control-input-wrapper">
          <NumberInput
            id="width"
            value={printWidth}
            onChange={handleWidthChange}
            disabled={isGenerating}
            step={0.1}
            min={0.1}
            className={errors.width ? styles.error : ''}
          />
        </div>
        {errors.width && <span className={styles.errorText}>{errors.width}</span>}
      </div>

      {/* Height */}
      <div className="style-control">
        <label htmlFor="height" className="style-label">Height</label>
        <div className="style-control-input-wrapper">
          <NumberInput
            id="height"
            value={printHeight}
            onChange={handleHeightChange}
            disabled={isGenerating}
            step={0.1}
            min={0.1}
            className={errors.height ? styles.error : ''}
          />
        </div>
        {errors.height && <span className={styles.errorText}>{errors.height}</span>}
      </div>

      {/* DPI */}
      <div className="style-control">
        <label htmlFor="dpi" className="style-label">DPI</label>
        <div className="style-control-input-wrapper">
          <NumberInput
            id="dpi"
            value={printDpi}
            onChange={handleDpiChange}
            disabled={isGenerating}
            step={1}
            min={50}
            max={600}
            className={errors.dpi ? styles.error : ''}
            style={{ maxWidth: '100px' }}
          />
        </div>
        {errors.dpi && <span className={styles.errorText}>{errors.dpi}</span>}
      </div>

      {/* Export Dimensions Info */}
      {printWidth > 0 && printHeight > 0 && printDpi > 0 && (() => {
        const sizeCheck = checkCanvasSizeCapped();
        const conversionFactor = printUnit === 'mm' ? 96 / 25.4 : 96;
        const finalWidthPx = Math.round(conversionFactor * printWidth * (printDpi / 96));
        const finalHeightPx = Math.round(conversionFactor * printHeight * (printDpi / 96));
        
        if (sizeCheck.isCapped) {
          return (
            <FeedbackBox
              statusType="warning"
              title="Canvas too big"
              message={`Requested: ${finalWidthPx} × ${finalHeightPx} pixels. Capped at ${sizeCheck.maxSize}px by WebGL`}
              icon={null}
            />
          );
        } else {
          return (
            <FeedbackBox
              statusType="info"
              title="Export dimensions"
              message={`${finalWidthPx} × ${finalHeightPx} pixels`}
              icon={null}
            />
          );
        }
      })()}

      {/* Error Messages */}
      {errors.general && (
        <div className={styles.errorMessage}>{errors.general}</div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: 'var(--spacing-s)' }}>
        <MenuButton
          onClick={handleGenerate}
          disabled={isGenerating || hasErrors || !mapInstance}
          icon={isGenerating ? <Loader2 size={16} className={styles.spinner} /> : <Printer size={16} />}
          style={{ flex: 1 }}
        >
          {isGenerating ? 'Generating...' : 'Print!'}
        </MenuButton>
        
        <MenuButton
          onClick={resetPrintConfig}
          disabled={isGenerating}
          style={{ flex: 1 }}
        >
          Reset
        </MenuButton>
      </div>
    </div>
  );
};

export default PrintMapsPanel;